import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins, ShieldCheck, X, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { acceptConsent, getConsentStatus } from "../lib/diploksClient";

/**
 * DiploksConsentModal — singleton.
 * Listens for `diploks:request-consent` global events and shows the
 * Activate Diploks Usage modal. The first time a user attempts to consume
 * Diploks, `lib/diploksClient.consume()` returns code='consent_required';
 * the caller dispatches the event and awaits the `diploks:consent-granted`
 * resolution.
 */
export default function DiploksConsentModal() {
  const [open, setOpen] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Caller-supplied resolver — fires on accept/cancel so the caller can
  // continue/abort its premium-feature flow.
  const [resolver, setResolver] = useState(null);

  const fetchPolicy = useCallback(async () => {
    try {
      const r = await api.get("/diploks/usage-consent/policy");
      setPolicy(r.data);
    } catch {
      setPolicy(null);
    }
  }, []);

  useEffect(() => {
    fetchPolicy();
    const onOpen = async (e) => {
      // Refresh policy in case the admin published a new version since
      // the page loaded.
      await fetchPolicy();
      // Re-check live status — the user may have already accepted on
      // another tab.
      try {
        const s = await getConsentStatus();
        if (!s.needs_consent) {
          if (e.detail?.resolve) e.detail.resolve({ ok: true });
          return;
        }
      } catch { /* fall through to modal */ }
      setResolver(() => e.detail?.resolve || null);
      setAccept(false);
      setOpen(true);
    };
    window.addEventListener("diploks:request-consent", onOpen);
    return () => window.removeEventListener("diploks:request-consent", onOpen);
  }, [fetchPolicy]);

  const finish = (granted) => {
    setOpen(false);
    if (resolver) resolver({ ok: granted });
    setResolver(null);
  };

  const submit = async () => {
    if (!accept) return;
    setSubmitting(true);
    try {
      await acceptConsent({ scope: "personal" });
      toast.success("Diploks Usage activated. You're all set!");
      finish(true);
    } catch (e) {
      toast.error("Could not activate Diploks usage. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !policy) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="diploks-consent-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
      >
        <div className="px-6 py-5 bg-gradient-to-r from-[#0A192F] via-[#0F2C50] to-[#0F4C81] text-white relative">
          <button
            type="button"
            onClick={() => finish(false)}
            data-testid="diploks-consent-close"
            className="absolute top-3 right-3 text-white/70 hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center">
              <Coins className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                One-time activation
              </p>
              <h2
                className="text-xl font-bold tracking-tight"
                data-testid="diploks-consent-title"
              >
                {policy.title || "Activate Diploks Usage"}
              </h2>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <p
            className="text-sm text-slate-700 leading-relaxed"
            data-testid="diploks-consent-message"
          >
            {policy.message}
          </p>

          <label
            className="mt-5 flex items-start gap-3 cursor-pointer rounded-lg border border-slate-200 p-3 hover:border-slate-300"
            data-testid="diploks-consent-checkbox-row"
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
              data-testid="diploks-consent-checkbox"
            />
            <span className="text-xs text-slate-700 leading-relaxed">
              {policy.checkbox_label}
            </span>
          </label>

          <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            You can review every Diploks usage in your Wallet &gt; Ledger.
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => finish(false)}
            data-testid="diploks-consent-cancel"
            className="text-xs text-slate-600 hover:text-slate-900 px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!accept || submitting}
            data-testid="diploks-consent-accept"
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded-md hover:bg-[#0F2C50] disabled:opacity-50"
          >
            {submitting ? "Activating…" : "Activate Diploks Usage"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Public helper — request consent.  Returns a Promise that resolves to
 * `{ok: boolean}` once the user accepts or cancels.
 *   const { ok } = await requestDiploksConsent();
 *   if (!ok) return;
 */
export function requestDiploksConsent() {
  return new Promise((resolve) => {
    window.dispatchEvent(
      new CustomEvent("diploks:request-consent", { detail: { resolve } }),
    );
  });
}
